import React, { Fragment } from 'react';
import { fill, get } from 'lodash';
import classnames from 'classnames';

import styles from './RankCardStyles';
import blankStyles from './BlankRankCardStyles';

import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import Badge from 'buyside/components/presentation/Badges/Badge/badge';
import DirectionIcon from './DirectionIcon/DirectionIcon';
import MessageButton from 'buyside/components/presentation/MessageModal/messageButton';
import FacebookButton from 'buyside/components/presentation/SocialMediaShareButtons/FacebookButton';
import FollowButton, {
  FollowableType
} from 'buyside/components/presentation/FollowButton/followButton';
import { BUTTON_SIZES } from 'buyside/components/presentation/Button/button';

import LinkedInButton from 'buyside/components/presentation/SocialMediaShareButtons/LinkedInButton';
import TinyIdeaCard from './TinyIdeaCard/tinyIdeaCard';
import TwitterButton from 'buyside/components/presentation/SocialMediaShareButtons/TwitterButton';
import { RankingIndexJoyrideTarget } from 'buyside/components/presentation/Joyride/RankingIndexJoyride/rankingIndexJoyrideHelper';

import {
  ideaUrl,
  userUrl,
  userIdeaUrl,
  employerProfileUrl,
  defaultUserImageUrl
} from 'buyside/util/urlHelper';
import { formatCategory } from 'buyside/util/rankCategoryHelper';

const Column = ({ className, children, joyrideTarget }) => {
  return (
    <div className={className ? className : styles.col} data-joyride-target={joyrideTarget}>
      {children}
    </div>
  );
};

const Row = ({ className, children }) => {
  return <div className={className ? className : styles.row}>{children}</div>;
};

const Badges = ({ badges }) => {
  return (
    <div className={styles.badgesContainer}>
      {badges.map((badge, idx) => {
        return badge.visible ? <Badge key={idx} badgeInfo={badge} /> : '';
      })}
    </div>
  );
};

const RecentIdeaCard = ({ recentIdea, isBlank, showIdeaDetails }) => {
  const isPairTrade = recentIdea.isPairTrade;

  if (isBlank) {
    return <TinyIdeaCard isBlank={isBlank} />;
  }
  return (
    <TinyIdeaCard
      primarySymbols={recentIdea.positions.map(position => position.primarySymbol)}
      totalReturn={recentIdea.totalReturn}
      ideaUrl={ideaUrl(recentIdea.id)}
      isPairTrade={isPairTrade}
      redacted={!showIdeaDetails}
    />
  );
};

const TinyIdeaCards = ({ ideas, user, isBlank, canViewUserDetails, showIdeaDetails }) => {
  let ideaCards;
  if (isBlank) {
    ideaCards = fill(Array(3), ' ').map((_v, idx) => <TinyIdeaCard key={idx} isBlank={isBlank} />);
  } else {
    ideaCards = ideas.map((idea, idx) => {
      const { isPairTrade } = idea;
      return (
        <div key={idx}>
          <TinyIdeaCard
            primarySymbols={idea.positions.map(position => position.primarySymbol)}
            totalReturn={idea.totalReturn}
            ideaUrl={ideaUrl(idea.id)}
            isPairTrade={isPairTrade}
            redacted={!showIdeaDetails}
          />
        </div>
      );
    });
  }

  return (
    <Fragment>
      <div className={styles.topIdeasTitle}>
        <span className={styles.topIdeasLabel}>Top Ideas:</span>
        <span className={styles.topIdeasLink}>
          {!isBlank && user.displayName && (
            <a href={userIdeaUrl(user.id)}>
              {canViewUserDetails
                ? `View all of ${user.displayName ? user.displayName.split(' ')[0] : ''}'s Ideas`
                : 'View all ideas by this member'}
            </a>
          )}
        </span>
      </div>
      <div className={styles.topIdeasContainer}>{ideaCards}</div>
    </Fragment>
  );
};

const ShareableRankDescription = ({ isCurrentUserCard, children }) => {
  if (isCurrentUserCard) {
    return (
      <Column className={`${styles.col} ${styles.currentUserShareableTextCol}`}>
        <div className={`${styles.shareableRankingText} ${styles.currentUserShareableRankText}`}>
          Share&nbsp;your ranking card&nbsp;
          <span className={styles.shareableRankingText}>with your friends on social networks</span>
        </div>
        {children}
        <div className={styles.currentUserShareableRankNote}>
          Note: We will not share ranking change information from the previous month
        </div>
      </Column>
    );
  } else {
    return <div className={styles.shareableRankingText}>Share&nbsp;this ranking</div>;
  }
};

const RankCard = ({
  badgeRelationships,
  canViewUserDetails,
  category,
  isBlank = false,
  isBlankOptions = {},
  isCurrentUserCard = false,
  isFirstRankCard,
  isPublic,
  rank,
  showIdeaDetails,
  topIdeas
}) => {
  let rankNum = '';
  let rankString = '';
  let direction = '';

  if (isBlank) {
    rankNum = get(isBlankOptions, 'rankNum', '');
    rankString = get(isBlankOptions, 'rankString', '');
  } else if (get(rank, 'rankString')) {
    rankNum = rank.rankString.split(' ')[0];
    rankString = rank.rankString.slice(rank.rankString.indexOf(' ')).trim();

    if (rank.viewDeltaString) direction = rank.viewDeltaString.split(' ')[0];
    if (category) rankString = formatCategory(category);
  }

  const shareableRankingText = `${get(rank, 'user.displayName', '')} is the ${get(
    rank,
    'rankString',
    ''
  )} Analyst on SumZero`;
  const shareableImageUrl = get(rank, 'shareableImageUrl', '');

  return (
    <div className={styles.rankCardContainer}>
      <Column className={`${styles.col} ${styles.leftCol}`}>
        <Row className={`${styles.row} ${styles.customRow}`}>
          <Column>
            <Row>
              <div className={styles.rankingPositionContainer}>
                <div className={styles.rankingPosition}>{rankNum}</div>
                <div className={styles.rankingPositionCategory}>{rankString}</div>
              </div>
              <div className={styles.profileImageContainer}>
                <img
                  className={styles.profileImage}
                  src={
                    canViewUserDetails
                      ? get(rank, 'user.profileImageUrl', defaultUserImageUrl())
                      : defaultUserImageUrl()
                  }
                />
                {!isCurrentUserCard && canViewUserDetails && (
                  <Fragment>
                    <div className={styles.shareableButtons}>
                      <FacebookButton shareUrl={shareableImageUrl} />
                      <TwitterButton shareText={shareableRankingText} />
                      <LinkedInButton shareText={shareableImageUrl} />
                    </div>
                    <ShareableRankDescription isCurrentUserCard={isCurrentUserCard} />
                  </Fragment>
                )}
              </div>
            </Row>
          </Column>
          <Column className={`${styles.col} ${styles.customCol}`}>
            <div className={classnames(styles.rankingName, { [blankStyles.rankingName]: isBlank })}>
              {canViewUserDetails ? (
                <a href={get(rank, 'user.id') ? userUrl(rank.user.id) : ''}>
                  {get(rank, 'user.displayName', '')}
                </a>
              ) : (
                <AnonymousUserDisplay
                  anonUser={{ title: get(rank, 'user.mostRecentEmployer.title', '') }}
                />
              )}
            </div>
            <div
              className={classnames(styles.rankingEmployer, {
                [blankStyles.rankingEmployer]: isBlank
              })}
            >
              {canViewUserDetails ? (
                <a
                  href={
                    get(rank, 'user.mostRecentEmployer.employer.id')
                      ? employerProfileUrl(rank.user.mostRecentEmployer.employer.id)
                      : ''
                  }
                >
                  {get(rank, 'user.currentEmployerName', '')}
                </a>
              ) : (
                <>
                  {get(rank, 'user.mostRecentEmployer.currentEmployer') && (
                    <div>
                      {get(rank, 'user.mostRecentEmployer.employer.aumRange')}{' '}
                      {get(rank, 'user.mostRecentEmployer.employer.fundType')}
                    </div>
                  )}
                </>
              )}
            </div>
            {!isBlank && (
              <Fragment>
                <div className={styles.rankingCity}>{get(rank, 'user.profileCity')}</div>
                <div className={styles.rankingMovement}>
                  <DirectionIcon direction={direction} />
                  {rank.viewDeltaString}
                </div>
              </Fragment>
            )}
            {isCurrentUserCard || isBlank || isPublic ? null : (
              <div className={styles.buttonsContainer}>
                <FollowButton
                  type={FollowableType.USER}
                  id={get(rank, 'user.id', '')}
                  size={BUTTON_SIZES.TINY}
                  joyrideTarget={isFirstRankCard ? RankingIndexJoyrideTarget.FOLLOW_MEMBER : ''}
                />
                <MessageButton
                  isCurrentEmployer={get(rank, 'user.mostRecentEmployer.currentEmployer', true)}
                  receiverEmployerName={get(rank, 'user.currentEmployerName', '')}
                  receiverId={get(rank, 'user.id', '')}
                  receiverName={get(rank, 'user.displayName', '')}
                  showUpsell
                />
              </div>
            )}
          </Column>
        </Row>
        {isCurrentUserCard && !isBlank && shareableImageUrl && shareableImageUrl.length && (
          <Row>
            <ShareableRankDescription isCurrentUserCard={isCurrentUserCard}>
              <div className={styles.shareableButtons}>
                <FacebookButton shareUrl={shareableImageUrl} />
                <TwitterButton shareText={shareableRankingText} />
                <LinkedInButton shareText={shareableImageUrl} />
              </div>
            </ShareableRankDescription>
          </Row>
        )}
      </Column>
      <Column
        joyrideTarget={isFirstRankCard ? RankingIndexJoyrideTarget.TOP_MOST_RECENT_IDEAS : ''}
      >
        <div>
          <TinyIdeaCards
            user={get(rank, 'user', {})}
            ideas={topIdeas}
            isBlank={isBlank}
            canViewUserDetails={canViewUserDetails}
            showIdeaDetails={showIdeaDetails}
          />
        </div>
        <div className={styles.recentIdeaContainer}>
          <div className={styles.recentIdeaCard}>
            <div className={styles.topIdeasLabel}>Most Recent Idea:</div>
            <div>
              <RecentIdeaCard
                recentIdea={get(rank, 'user.recentIdea', {})}
                badges={badgeRelationships}
                isBlank={isBlank}
                showIdeaDetails={showIdeaDetails}
              />
            </div>
          </div>
          <div>
            {!isBlank || (badgeRelationships && badgeRelationships.length) ? (
              <div className={styles.topIdeasLabel}>Badges:</div>
            ) : null}
            <Badges badges={isBlank ? [] : badgeRelationships} />
          </div>
        </div>
      </Column>
    </div>
  );
};

export default RankCard;
